/**
 * Deterministic Typst Sanitizer (Frontend Client Copy)
 * 
 * Provides fast, zero-dependency, pure-function syntax cleanup for LLM-generated Typst markup.
 * Designed to eliminate >95% of common LLM compilation errors before hitting the compiler.
 */

export interface SanitizeResult {
  content: string;
  fixesApplied: string[];
}

/**
 * Sanitizes and repairs common Typst markup issues in LLM-generated text.
 */
export function sanitizeTypstMarkup(rawContent: string): SanitizeResult {
  if (!rawContent || typeof rawContent !== "string") {
    return { content: "", fixesApplied: [] };
  }

  const fixesApplied: string[] = [];
  let content = rawContent.replace(/\r\n/g, "\n");

  // 1. Newline Unescaping
  if (content.includes("\\n")) {
    let unescapeIterations = 0;
    while (content.includes("\\n") && unescapeIterations < 5) {
      content = content.replace(/\\n/g, "\n");
      unescapeIterations++;
    }
    if (unescapeIterations > 0) {
      fixesApplied.push(`Unescaped literal \\n (${unescapeIterations} passes)`);
    }
  }

  // 2. Escape Currency & Standalone Dollar Signs ($ -> \$)
  const dollarBefore = content;
  content = content.replace(/(?<!\\)\$(\s*\d[\d,.]*)/g, (_m, p1) => "\\" + "$" + p1);
  content = content.replace(/(?<!\\)\$(?=\s|[/a-zA-Z]|$)/g, () => "\\" + "$");

  if (content !== dollarBefore) {
    fixesApplied.push("Escaped currency/standalone dollar signs");
  }

  // 3. Escape Email Addresses
  const emailBefore = content;
  content = content.replace(
    /([a-zA-Z0-9._%+-]+)@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    "$1\\@$2"
  );
  if (content !== emailBefore) {
    fixesApplied.push("Escaped email @ symbols");
  }

  // 4. Fix Common LLM Hallucinations
  if (/table\.of\.contents\(\)/.test(content)) {
    content = content.replace(/#?table\.of\.contents\(\)/g, "#outline()");
    fixesApplied.push("Replaced table.of.contents() with #outline()");
  }

  if (/table\.of\(/.test(content)) {
    content = content.replace(/table\.of\(/g, "table(");
    fixesApplied.push("Replaced table.of() with table()");
  }

  if (/line\([^)]*indent:[^)]*\)/.test(content)) {
    content = content.replace(/#?line\([^)]*indent:[^)]*\)/g, "#line(length: 100%)");
    fixesApplied.push("Fixed invalid line(indent:) parameter");
  }

  content = content.replace(/(?<![#a-zA-Z0-9_])datetime\s*\(/g, "#datetime(");
  content = content.replace(/#out(?![a-zA-Z0-9_()])/g, "#outline()");
  content = content.replace(/\\\\(?=\s)/g, "\\");

  // 5. State Machine: Strip Illegal '#' in Code & Parameter Contexts
  content = cleanIllegalHashes(content, fixesApplied);

  // 6. Balance Unclosed Delimiters
  content = balanceDelimiters(content, fixesApplied);

  return {
    content,
    fixesApplied,
  };
}

function cleanIllegalHashes(input: string, fixes: string[]): string {
  let result = "";
  let inString = false;
  let inComment = false;
  let inBlockComment = false;
  let escapeNext = false;
  let fixCount = 0;

  const stack: ("CONTENT" | "CODE")[] = ["CONTENT"];

  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    const nextChar = i + 1 < input.length ? input[i + 1] : "";

    if (escapeNext) {
      escapeNext = false;
      result += char;
      continue;
    }
    if (char === "\\") {
      escapeNext = true;
      result += char;
      continue;
    }

    if (!inString && !inBlockComment && char === "/" && nextChar === "/") {
      inComment = true;
      result += "//";
      i++;
      continue;
    }
    if (inComment && char === "\n") {
      inComment = false;
      result += char;
      continue;
    }

    if (!inString && !inComment && char === "/" && nextChar === "*") {
      inBlockComment = true;
      result += "/*";
      i++;
      continue;
    }
    if (inBlockComment && char === "*" && nextChar === "/") {
      inBlockComment = false;
      result += "*/";
      i++;
      continue;
    }

    if (inComment || inBlockComment) {
      result += char;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      result += char;
      continue;
    }

    if (inString) {
      result += char;
      continue;
    }

    const currentMode = stack[stack.length - 1] || "CONTENT";

    if (char === "(" || char === "{") {
      stack.push("CODE");
      result += char;
    } else if (char === ")" || char === "}") {
      if (stack.length > 1) {
        stack.pop();
      }
      result += char;
    } else if (char === "[") {
      stack.push("CONTENT");
      result += char;
    } else if (char === "]") {
      if (stack.length > 1) {
        stack.pop();
      }
      result += char;
    } else if (char === "#") {
      if (currentMode === "CODE") {
        fixCount++;
        continue;
      } else {
        result += char;
      }
    } else {
      result += char;
    }
  }

  if (fixCount > 0) {
    fixes.push(`Removed ${fixCount} illegal '#' symbols in code/parameter expressions`);
  }

  return result;
}

function balanceDelimiters(input: string, fixes: string[]): string {
  const stack: { char: string; index: number }[] = [];
  let inString = false;
  let inComment = false;
  let inBlockComment = false;
  let escapeNext = false;

  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    const nextChar = i + 1 < input.length ? input[i + 1] : "";

    if (escapeNext) {
      escapeNext = false;
      continue;
    }
    if (char === "\\") {
      escapeNext = true;
      continue;
    }
    if (!inString && !inBlockComment && char === "/" && nextChar === "/") {
      inComment = true;
      i++;
      continue;
    }
    if (inComment && char === "\n") {
      inComment = false;
      continue;
    }
    if (!inString && !inComment && char === "/" && nextChar === "*") {
      inBlockComment = true;
      i++;
      continue;
    }
    if (inBlockComment && char === "*" && nextChar === "/") {
      inBlockComment = false;
      i++;
      continue;
    }
    if (inComment || inBlockComment) continue;

    if (char === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;

    if (char === "(" || char === "[" || char === "{") {
      stack.push({ char, index: i });
    } else if (char === ")" || char === "]" || char === "}") {
      const top = stack[stack.length - 1];
      if (
        (char === ")" && top?.char === "(") ||
        (char === "]" && top?.char === "[") ||
        (char === "}" && top?.char === "{")
      ) {
        stack.pop();
      }
    }
  }

  if (stack.length > 0) {
    let closing = "";
    while (stack.length > 0) {
      const item = stack.pop()!;
      if (item.char === "(") closing += ")";
      else if (item.char === "[") closing += "]";
      else if (item.char === "{") closing += "}";
    }
    fixes.push(`Auto-balanced ${closing.length} unclosed delimiters at document end (${closing})`);
    return input + "\n" + closing;
  }

  return input;
}
